import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, ArrowRight, ArrowLeft, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import BackToHome from '../components/BackToHome';

const ResetPassword = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await api.get(`/auth/verify-reset-token?token=${token}`);
        setTokenValid(response.data.valid);
        setEmail(response.data.email || '');
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(language === 'ar' ? 'كلمة المرور غير متطابقة' : 'Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 4) {
      toast.error(language === 'ar' ? 'كلمة المرور يجب أن تكون 4 أحرف على الأقل' : 'Password must be at least 4 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: passwordData.newPassword
      });
      setSuccess(true);
      toast.success(language === 'ar' ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل تغيير كلمة المرور' : 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  const Arrow = language === 'ar' ? ArrowLeft : ArrowRight;

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">
            {language === 'ar' ? 'جاري التحقق...' : 'Verifying...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <BackToHome />
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">KE Gents Saloon</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
          </p>
        </div>

        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-8">
          {!token || !tokenValid ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                {language === 'ar' ? 'رابط غير صالح' : 'Invalid Link'}
              </h2>
              <p className="text-muted-foreground text-sm mb-4">
                {language === 'ar' 
                  ? 'رابط استعادة كلمة المرور غير صالح أو منتهي الصلاحية.' 
                  : 'The password reset link is invalid or has expired.'}
              </p>
              <Link to="/forgot-password">
                <Button className="bg-primary text-primary-foreground">
                  {language === 'ar' ? 'طلب رابط جديد' : 'Request New Link'}
                </Button>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                {language === 'ar' ? 'تم بنجاح!' : 'Success!'}
              </h2>
              <p className="text-muted-foreground text-sm mb-4">
                {language === 'ar' 
                  ? 'تم تغيير كلمة المرور بنجاح. يمكنك الآن تسجيل الدخول.' 
                  : 'Your password has been changed successfully. You can now log in.'}
              </p>
              <Link to="/login">
                <Button className="bg-primary text-primary-foreground">
                  {language === 'ar' ? 'تسجيل الدخول' : 'Log In'}
                  <Arrow className="w-4 h-4 ms-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-semibold mb-2">
                  {language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
                </h2>
                {email && (
                  <p className="text-muted-foreground text-sm" dir="ltr">
                    {email}
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                  </label>
                  <Input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="bg-secondary/50 border-white/10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                  </label>
                  <Input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="bg-secondary/50 border-white/10"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary text-primary-foreground"
                  disabled={loading}
                >
                  {loading ? (
                    language === 'ar' ? 'جاري الحفظ...' : 'Saving...'
                  ) : (
                    <>
                      {language === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}
                      <Arrow className="w-4 h-4 ms-2" />
                    </>
                  )}
                </Button>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <Link 
              to="/login" 
              className="text-primary hover:underline text-sm inline-flex items-center gap-2"
            >
              {language === 'ar' ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
              {language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
